import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import timerIconSrc from 'images/icons/emoji-clock.png';
import flameIconSrc from 'images/icons/emoji-flame.png';
import { Text } from '@reservamos/elements';

/**
 * PromoUrgencyBanner component.
 * Renders a flash-sale alert banner with promotional copy and an ends-soon badge.
 *
 * @param {object} props - Component props.
 * @param {number} props.scheduleCount - Number of schedules with an active discount today.
 * @param {number} props.discountPercent - Maximum discount percentage to highlight.
 * @param {number} props.ticketsSold - Number of tickets sold during the promotion.
 * @param {string} [props.endsSoonLabel] - Optional override for the ends-soon badge label.
 * @param {string} [props.className] - Optional extra classes for the root element.
 * @returns {JSX.Element} The flash sale alert banner.
 */
const PromoUrgencyBanner = ({
  scheduleCount,
  discountPercent,
  ticketsSold,
  endsSoonLabel,
  className,
}) => {
  const { t } = useTranslation('search');
  const messageKey =
    ticketsSold > 0 ? 'flash_sale_alert_message' : 'flash_sale_alert_message_without_tickets';
  const rootClassName = [
    'flex w-full items-center justify-between rounded-xl bg-[#EB2E00] text-white md:pr-3 pr-2',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} role="status">
      <div className="flex flex-1 items-center gap-2">
        <div className="flex h-11 md:w-11 w-20 items-center justify-center rounded-lg bg-black/15">
          <img src={flameIconSrc} alt="" className="h-5 w-5" aria-hidden="true" />
        </div>
        <Text size="S" mobileSize="XXS" color="white" weight="semibold">
          {t(messageKey, {
            count: scheduleCount,
            scheduleCount,
            percent: discountPercent,
            ticketsSold,
          })}
        </Text>
      </div>
      <div className="flex items-center gap-1.5 rounded-full bg-white/20 p-2">
        <img src={timerIconSrc} alt="" className="h-3 w-3" aria-hidden="true" />
        <Text size="XS" mobileSize="XXS" color="white" weight="bold">
          {endsSoonLabel || t('flash_sale_alert_ends_soon')}
        </Text>
      </div>
    </div>
  );
};

PromoUrgencyBanner.propTypes = {
  scheduleCount: PropTypes.number,
  discountPercent: PropTypes.number,
  ticketsSold: PropTypes.number,
  endsSoonLabel: PropTypes.string,
  className: PropTypes.string,
};

PromoUrgencyBanner.defaultProps = {
  scheduleCount: 0,
  discountPercent: 0,
  ticketsSold: 0,
  endsSoonLabel: null,
  className: null,
};

export default PromoUrgencyBanner;
